/**
 * Form Placeholders Utility
 * 
 * Provides functions to build HTML components for custom form placeholders:
 * - {FIELDS} - Dynamic form fields
 * - {FACIAL} - Facial capture component
 * - {LEGAL} - Legal consent checkboxes
 * - {SUBMIT} - Submit button
 * - {field_<name>} - Individual field placeholders
 */

/**
 * Build HTML for a single form field (individual placeholder)
 * @param {Object} field - Field object with fieldName, fieldLabel, fieldType, isRequired, options
 * @returns {string} HTML string for the field
 */
export const buildIndividualFieldHtml = (field) => {
  if (!field) return '';
  
  const required = field.isRequired ? 'required' : '';
  const requiredStar = field.isRequired ? '<span style="color: #dc2626;">*</span>' : '';
  const safeId = `field-${field.fieldName}`;
  
  switch (field.fieldType) {
    case 'text':
    case 'email':
    case 'number':
    case 'date':
      return `
        <div class="form-field">
          <label for="${safeId}">${field.fieldLabel} ${requiredStar}</label>
          <input 
            type="${field.fieldType}" 
            id="${safeId}" 
            name="${field.fieldName}"
            ${required}
            placeholder="Ingrese ${field.fieldLabel.toLowerCase()}"
          />
        </div>
      `;
      
    case 'tel':
      return `
        <div class="form-field">
          <label for="${safeId}">${field.fieldLabel} ${requiredStar}</label>
          <input 
            type="tel" 
            id="${safeId}" 
            name="${field.fieldName}"
            ${required}
            placeholder="+51 999 999 999"
          />
        </div>
      `;
      
    case 'textarea':
      return `
        <div class="form-field">
          <label for="${safeId}">${field.fieldLabel} ${requiredStar}</label>
          <textarea 
            id="${safeId}" 
            name="${field.fieldName}"
            ${required}
            rows="4"
            placeholder="Ingrese ${field.fieldLabel.toLowerCase()}"
          ></textarea>
        </div>
      `;
      
    case 'select':
      const options = field.options ? JSON.parse(field.options) : [];
      return `
        <div class="form-field">
          <label for="${safeId}">${field.fieldLabel} ${requiredStar}</label>
          <select id="${safeId}" name="${field.fieldName}" ${required}>
            <option value="">Seleccione una opción</option>
            ${options.map(opt => `<option value="${opt}">${opt}</option>`).join('')}
          </select>
        </div>
      `;
      
    case 'checkbox':
      return `
        <div class="form-field checkbox-field">
          <label>
            <input type="checkbox" name="${field.fieldName}" value="true" ${required} />
            ${field.fieldLabel}
          </label>
        </div>
      `;
      
    default:
      return `
        <div class="form-field">
          <label for="${safeId}">${field.fieldLabel} ${requiredStar}</label>
          <input 
            type="text" 
            id="${safeId}" 
            name="${field.fieldName}"
            ${required}
          />
        </div>
      `;
  }
};

/**
 * Build HTML for dynamic form fields based on project field configuration
 * @param {Array} fields - Array of field objects from project configuration
 * @returns {string} HTML string for form fields
 */
export const buildFieldsHtml = (fields = []) => {
  if (!fields || fields.length === 0) {
    return '<p class="no-fields-msg">No hay campos configurados para este formulario.</p>';
  }

  const fieldsHtml = fields
    .filter(f => f.fieldType !== 'facial_recognition' && f.fieldType !== 'photo')
    .map(field => {
      const required = field.isRequired ? 'required' : '';
      const requiredStar = field.isRequired ? '<span style="color: #dc2626;">*</span>' : '';
      
      switch (field.fieldType) {
        case 'text':
        case 'email':
        case 'number':
        case 'date':
          return `
            <div class="form-field">
              <label for="field-${field.fieldName}">${field.fieldLabel} ${requiredStar}</label>
              <input 
                type="${field.fieldType}" 
                id="field-${field.fieldName}" 
                name="${field.fieldName}"
                ${required}
                placeholder="Ingrese ${field.fieldLabel.toLowerCase()}"
              />
            </div>
          `;
          
        case 'tel':
          return `
            <div class="form-field">
              <label for="field-${field.fieldName}">${field.fieldLabel} ${requiredStar}</label>
              <input 
                type="tel" 
                id="field-${field.fieldName}" 
                name="${field.fieldName}"
                ${required}
                placeholder="+51 999 999 999"
              />
            </div>
          `;
          
        case 'textarea':
          return `
            <div class="form-field">
              <label for="field-${field.fieldName}">${field.fieldLabel} ${requiredStar}</label>
              <textarea 
                id="field-${field.fieldName}" 
                name="${field.fieldName}"
                ${required}
                rows="4"
                placeholder="Ingrese ${field.fieldLabel.toLowerCase()}"
              ></textarea>
            </div>
          `;
          
        case 'select':
          const options = field.options ? JSON.parse(field.options) : [];
          return `
            <div class="form-field">
              <label for="field-${field.fieldName}">${field.fieldLabel} ${requiredStar}</label>
              <select id="field-${field.fieldName}" name="${field.fieldName}" ${required}>
                <option value="">Seleccione una opción</option>
                ${options.map(opt => `<option value="${opt}">${opt}</option>`).join('')}
              </select>
            </div>
          `;
          
        case 'checkbox':
          return `
            <div class="form-field checkbox-field">
              <label>
                <input type="checkbox" name="${field.fieldName}" value="true" ${required} />
                ${field.fieldLabel}
              </label>
            </div>
          `;
          
        default:
          return `
            <div class="form-field">
              <label for="field-${field.fieldName}">${field.fieldLabel} ${requiredStar}</label>
              <input 
                type="text" 
                id="field-${field.fieldName}" 
                name="${field.fieldName}"
                ${required}
              />
            </div>
          `;
      }
    })
    .join('');

  return fieldsHtml;
};

/**
 * Build HTML for facial recognition capture placeholder
 * Note: This is a placeholder for the iframe. The actual liveness detection
 * is handled by the parent component (PublicForm.js)
 * @returns {string} HTML string for facial placeholder
 */
export const buildFacialHtml = () => {
  return `
    <div class="facial-section" style="margin: 20px 0;">
      <div class="facial-checkbox" style="display: flex; align-items: center; padding: 12px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px;">
        <input type="checkbox" id="facial_consent" name="facial_consent" value="true" style="margin-right: 10px; width: 20px; height: 20px;" />
        <label for="facial_consent" style="cursor: pointer; flex: 1;">
          <strong>Activar reconocimiento facial</strong> para acceso rápido sin QR
        </label>
      </div>
      <div id="facial_placeholder" style="display: none; margin-top: 16px; padding: 24px; background: #f9fafb; border: 2px dashed #d1d5db; border-radius: 8px; text-align: center; color: #6b7280;">
        <p>La captura facial se realizará después de enviar el formulario</p>
      </div>
      <script>
        (function() {
          const checkbox = document.getElementById('facial_consent');
          const placeholder = document.getElementById('facial_placeholder');
          if (checkbox && placeholder) {
            checkbox.addEventListener('change', function() {
              placeholder.style.display = this.checked ? 'block' : 'none';
            });
          }
        })();
      </script>
    </div>
  `;
};

/**
 * Build HTML for legal consent checkboxes
 * @param {string} termsUrl - URL to terms page
 * @param {string} privacyUrl - URL to privacy page
 * @returns {string} HTML string for legal checkboxes
 */
export const buildLegalHtml = (termsUrl = '/terminos', privacyUrl = '/privacidad') => {
  return `
    <div class="legal-section" style="margin: 20px 0;">
      <div class="legal-checkbox" style="display: flex; align-items: flex-start; padding: 12px; background: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px;">
        <input type="checkbox" id="accepted_terms" name="accepted_terms" value="true" required style="margin-top: 3px; margin-right: 10px; width: 18px; height: 18px;" />
        <label for="accepted_terms" style="font-size: 14px; color: #6b7280; line-height: 1.5;">
          He leído y acepto los 
          <a href="${termsUrl}" target="_blank" style="color: #3b82f6; text-decoration: underline;">Términos y Condiciones</a>
          y la 
          <a href="${privacyUrl}" target="_blank" style="color: #3b82f6; text-decoration: underline;">Política de Privacidad</a>.
          Soy mayor de edad y proporciono mis datos de forma voluntaria.
        </label>
      </div>
      <p id="terms_error" class="error-msg" style="display: none; margin-top: 8px;">
        Debe aceptar los términos para continuar
      </p>
    </div>
  `;
};

/**
 * Build HTML for submit button
 * @returns {string} HTML string for submit button
 */
export const buildSubmitHtml = () => {
  return `
    <div class="submit-section" style="margin: 24px 0;">
      <button type="submit" class="submit-btn" style="
        width: 100%;
        padding: 14px 24px;
        background: #3b82f6;
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s;
      ">
        Registrarse al Evento
      </button>
    </div>
  `;
};

/**
 * Inject placeholders into custom HTML
 * @param {string} html - Custom HTML content with placeholders
 * @param {Object} placeholders - Object containing placeholder values
 * @returns {string} HTML with placeholders replaced
 */
export const injectPlaceholders = (html, placeholders = {}) => {
  if (!html) return '';
  
  const {
    fieldsHtml = '',
    facialHtml = '',
    legalHtml = '',
    submitHtml = ''
  } = placeholders;
  
  return html
    .replace(/\{FIELDS\}/g, fieldsHtml)
    .replace(/\{FACIAL\}/g, facialHtml)
    .replace(/\{LEGAL\}/g, legalHtml)
    .replace(/\{SUBMIT\}/g, submitHtml);
};

/**
 * Inject all placeholder types (block and individual field)
 * @param {string} html - Custom HTML with placeholders
 * @param {Object} placeholders - Object with field_<name>, FIELDS, FACIAL, LEGAL, SUBMIT
 * @returns {string} HTML with all placeholders replaced
 */
export const injectAllPlaceholders = (html, placeholders = {}) => {
  if (!html) return '';
  
  let result = html;
  
  // Replace block placeholders first
  const blockKeys = ['FIELDS', 'FACIAL', 'LEGAL', 'SUBMIT'];
  for (const key of blockKeys) {
    if (placeholders[key]) {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      result = result.replace(regex, placeholders[key]);
    }
  }
  
  // Replace individual field placeholders {field_<name>}
  for (const [key, value] of Object.entries(placeholders)) {
    if (key.startsWith('field_')) {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      result = result.replace(regex, value);
    }
  }
  
  // Remove any remaining invalid field placeholders (fail silently)
  result = result.replace(/\{field_[^}]+\}/g, '');
  
  return result;
};

export default {
  buildFieldsHtml,
  buildFacialHtml,
  buildLegalHtml,
  buildSubmitHtml,
  injectPlaceholders,
  injectAllPlaceholders,
  buildIndividualFieldHtml
};
